import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Popconfirm, Row, Statistic, Table, Tag, Tooltip, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import * as api from '@apis/reservation-success';
import { ReservationSuccessBatchRun } from '@apis/reservation-success';
import { formatSlowestPhase } from '@utils/reservation-success-batch-phases';
import { RUN_STATUS_COLOR, invocationTypeLabel, runStatusLabel } from '@utils/reservation-success-labels';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';

const ReservationSuccessBatchRunsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const dashboardQuery = useQuery({
    queryKey: ['reservation-success-dashboard'],
    queryFn: () => api.getReservationSuccessMonitoringDashboard(),
    refetchInterval: 30000,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['reservation-success-batch-runs'],
    queryFn: () => api.listReservationSuccessBatchRuns({ limit: 50 }),
    refetchInterval: (query) =>
      (query.state.data?.data as ReservationSuccessBatchRun[] | undefined)?.some((r) => r.status === 'running')
        ? 5000
        : false,
  });

  const runMutation = useMutation({
    mutationFn: () => api.runReservationSuccessBatch(),
    onSuccess: () => {
      message.success('예약성사 배치 실행이 시작되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['reservation-success-batch-runs'] });
      refetch();
    },
    onError: () => message.error('배치 실행 실패'),
  });

  const runs: ReservationSuccessBatchRun[] = data?.data || [];
  const dashboard = dashboardQuery.data?.data;

  const columns: ColumnsType<ReservationSuccessBatchRun> = [
    { title: '실행 ID', dataIndex: 'runId', render: (v) => v.slice(0, 8) },
    {
      title: '상태',
      dataIndex: 'status',
      render: (v: string) => <Tag color={RUN_STATUS_COLOR[v] || 'default'}>{runStatusLabel(v)}</Tag>,
    },
    {
      title: <Tooltip title="이 시간 구간에 배치가 돌았는지 구분하는 키">기준 시간</Tooltip>,
      dataIndex: 'hourBucket',
    },
    {
      title: '호출',
      dataIndex: 'invocationType',
      render: (v: string) => invocationTypeLabel(v),
    },
    {
      title: '시작',
      dataIndex: 'startedAt',
      render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '소요',
      dataIndex: 'durationMs',
      render: (v) => (v != null ? `${Math.round(v / 1000)}초` : '-'),
    },
    {
      title: '가장 느린 구간',
      render: (_, r) => formatSlowestPhase(r.summary?.phaseDurationsMs),
    },
    {
      title: '회차',
      render: (_, r) => r.summary?.sessionCount ?? '-',
    },
    {
      title: '',
      render: (_, r) => (
        <Button type="link" onClick={() => navigate(`/reservation-success-batch-runs/${r.runId}`)}>
          상세
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title="배치 실행"
        description="그룹핑, 세션 로드, LLM 진단, 스냅샷, 알림을 한 번에 실행한 이력입니다. 진행 중이면 5초마다, 상태 카드는 30초마다 갱신됩니다."
        extra={
          <Popconfirm
            title="배치를 지금 실행할까요?"
            description="진행 중인 실행이 있으면 겹칠 수 있습니다."
            okText="실행"
            cancelText="취소"
            onConfirm={() => runMutation.mutate()}>
            <Button type="primary" loading={runMutation.isPending}>
              수동 실행
            </Button>
          </Popconfirm>
        }
      />

      {dashboard && (
        <>
          {dashboard.alerts.map((a, i) => (
            <Alert
              key={i}
              type={a.level === 'error' ? 'error' : 'warning'}
              message={a.message}
              showIcon
              style={{ marginBottom: 8 }}
            />
          ))}
          <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
            <Col span={6}>
              <Card>
                <Statistic title="정상 여부" value={dashboard.healthy ? '정상' : '이상'} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="마지막 성공"
                  value={
                    dashboard.lastSuccessfulRunAt ? dayjs(dashboard.lastSuccessfulRunAt).format('MM/DD HH:mm') : '-'
                  }
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="지연 알림" value={dashboard.staleAlert ? '있음' : '없음'} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="기준 시간" value={dashboard.currentHourBucket} />
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Table rowKey="runId" loading={isLoading} columns={columns} dataSource={runs} pagination={false} />
    </div>
  );
};

export default ReservationSuccessBatchRunsPage;
